import DossierAgendaCard from "./planning/DossierAgendaCard";
import DemarchesSyntheseCard from "./DemarchesSyntheseCard";
import React, { useCallback, useEffect, useState } from "react";
import { User, Users, Banknote, Mail, Phone, Home, Building, Euro, CalendarDays, Package } from "lucide-react";
import { api } from "../lib/api";
import { EditableField, AddFieldButton } from "./EditableField";
import DossierAiSummary from "./DossierAiSummary";
import AttachmentStrip from "./AttachmentStrip";
import ResteAVivreCard from "./ResteAVivreCard";
import MissingDocsCard from "./MissingDocsCard";
import DossierTicketsCard from "./DossierTicketsCard";
import InstallationSyntheseCard from "./InstallationSyntheseCard";
import DossierDocumentsCard from "./DossierDocumentsCard";
import DossierDiscussion from "./DossierDiscussion";
import DossierEmails from "./DossierEmails";
import DossierRingover from "./DossierRingover";
import DossierStreetView from "./DossierStreetView";
import DictationButton from "./DictationButton";

const CLIENT_FIELDS = [
  { key: "civilite", label: "Civilité" },
  { key: "nom", label: "Nom", bold: true },
  { key: "prenom", label: "Prénom", bold: true },
  { key: "adresse", label: "Adresse", icon: Home, span2: true },
  { key: "code_postal", label: "Code postal" },
  { key: "ville", label: "Ville" },
  { key: "email", label: "Email", icon: Mail, link: (v) => `mailto:${v}`, span2: true },
  { key: "telephone", label: "Téléphone", icon: Phone, link: (v) => `tel:${v}` },
  { key: "telephone_2", label: "Téléphone 2", icon: Phone, link: (v) => `tel:${v}` },
  { key: "date_naissance", label: "Date de naissance", icon: CalendarDays, dateFr: true },
  { key: "date_retractation", label: "Date de rétractation", icon: CalendarDays, dateFr: true },
];

const FIN_FIELDS = [
  { key: "produit", label: "Produit", icon: Package, bold: true },
  { key: "organisme_financier", label: "Organisme", icon: Building },
  { key: "prix_vente", label: "Prix de vente", icon: Euro, bold: true, display: (v) => (v ? `${v} €` : null) },
  { key: "date_signature_bdc", label: "Date signature", icon: CalendarDays },
];

function FieldsCard({ title, icon: Icon, section, fields, values, customDefs, customValues, onSaveField, onSaveCustom, onDeleteCustom, onCreateCustom, newFieldId, dossierId, onDictated, testid, grow }) {
  return (
    <div className={`card-flat p-5 ${grow ? "flex-1" : "h-full"}`} data-testid={testid}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="overline flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {title}
        </div>
        <DictationButton dossierId={dossierId} target={section} onApplied={onDictated} compact testid={`dictation-btn-${section}`} />
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {fields.map((f) => (
          <EditableField
            key={f.key}
            label={f.label}
            value={values[f.key] || ""}
            display={f.display ? f.display(values[f.key]) : undefined}
            icon={f.icon}
            bold={f.bold}
            span2={f.span2}
            link={f.link && values[f.key] ? f.link(values[f.key]) : null}
            onSave={(v) => onSaveField(f.key, v)}
            dateFr={f.dateFr}
            testid={`field-${f.key}`}
          />
        ))}
        {customDefs.filter((c) => c.section === section).map((c) => (
          <EditableField
            key={c.id}
            label={c.label}
            value={customValues[c.id] || ""}
            autoEdit={c.id === newFieldId}
            onSave={(v) => onSaveCustom(c.id, v)}
            onDelete={() => onDeleteCustom(c)}
            testid={`custom-field-${c.id}`}
          />
        ))}
        <AddFieldButton onCreate={(label) => onCreateCustom(section, label)} testid={`add-field-${section}`} />
      </div>
    </div>
  );
}

const CO_EMP_FIELDS = [
  { key: "nom", label: "Nom", bold: true },
  { key: "prenom", label: "Prénom", bold: true },
  { key: "telephone", label: "Téléphone", icon: Phone, link: (v) => `tel:${v}` },
  { key: "email", label: "Email", icon: Mail, link: (v) => `mailto:${v}` },
];

function CoEmprunteurCard({ dossierId, co, onSaved }) {
  const save = async (key, value) => {
    await api.patch(`/dossiers/${dossierId}/tab-data/finance`, { values: { [`co_emp_${key}`]: value } });
    onSaved?.();
  };
  const sameAddr = co.meme_adresse !== false;
  const addr = [co.adresse, co.code_postal, co.ville].filter(Boolean).join(" ");
  return (
    <div className="card-flat p-5" data-testid="co-emprunteur-card">
      <div className="overline flex items-center gap-1.5 mb-4"><Users className="w-3 h-3" /> Co-emprunteur</div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {CO_EMP_FIELDS.map((f) => (
          <EditableField key={f.key} label={f.label} value={co[f.key] || ""} icon={f.icon} bold={f.bold}
            link={f.link && co[f.key] ? f.link(co[f.key]) : null} onSave={(v) => save(f.key, v)} testid={`co-emp-field-${f.key}`} />
        ))}
        <div className="col-span-2 text-xs text-norea-textMuted flex items-center gap-1.5" data-testid="co-emp-address">
          <Home className="w-3 h-3" /> {sameAddr ? "Même adresse que le bénéficiaire" : addr || "Adresse non renseignée"}
        </div>
      </div>
    </div>
  );
}

export default function DossierSynthese({ dossier, onDossierUpdated, verifPopup }) {
  const ex = dossier.extraction || {};
  const phones = [ex.telephone, ex.telephone_2].filter(Boolean);
  const [customDefs, setCustomDefs] = useState([]);
  const [newFieldId, setNewFieldId] = useState(null);

  const loadDefs = useCallback(
    () => api.get("/configuration/custom-fields").then((r) => setCustomDefs(r.data.fields || [])).catch(() => {}),
    []
  );
  useEffect(() => { loadDefs(); }, [loadDefs]);

  const saveField = async (field, value) => {
    await api.patch(`/dossiers/${dossier.id}/fields`, { field, value });
    onDossierUpdated?.();
  };
  const saveCustom = async (field_id, value) => {
    await api.patch(`/dossiers/${dossier.id}/custom-fields`, { field_id, value });
    onDossierUpdated?.();
  };
  const createCustom = async (section, label) => {
    const { data } = await api.post("/configuration/custom-fields", { section, label });
    await loadDefs();
    setNewFieldId(data.field.id);
  };
  const deleteCustom = async (c) => {
    if (!window.confirm(`Supprimer le champ « ${c.label} » de TOUS les dossiers ? Les valeurs saisies seront perdues.`)) return;
    await api.delete(`/configuration/custom-fields/${c.id}`);
    loadDefs();
    onDossierUpdated?.();
  };

  const common = {
    values: { ...ex, produit: ex.produit || dossier.produit },
    customDefs,
    customValues: dossier.custom_fields || {},
    onSaveField: saveField,
    onSaveCustom: saveCustom,
    onDeleteCustom: deleteCustom,
    onCreateCustom: createCustom,
    newFieldId,
    dossierId: dossier.id,
    onDictated: onDossierUpdated,
  };

  return (
    <div className="space-y-5" data-testid="dossier-synthese">
      <DossierAiSummary dossierId={dossier.id} />
      <AttachmentStrip dossierId={dossier.id} reloadKey={dossier.updated_at} />

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch" data-testid="synthese-row-1">
        <div className="flex flex-col gap-5">
          <FieldsCard title="Informations client" icon={User} section="client" fields={CLIENT_FIELDS} testid="client-info-card" grow {...common} />
          {dossier.co_emprunteur?.actif && <CoEmprunteurCard dossierId={dossier.id} co={dossier.co_emprunteur} onSaved={onDossierUpdated} />}
        </div>
        <div className="flex flex-col gap-5">
          <FieldsCard title="Financement" icon={Banknote} section="financement" fields={FIN_FIELDS} testid="financing-card" grow {...common} />
          <MissingDocsCard dossier={dossier} onChanged={onDossierUpdated} verifPopup={verifPopup} />
        </div>
        <div className="lg:col-span-2 xl:col-span-1">
          <DossierStreetView dossier={dossier} />
        </div>
      </div>

      <InstallationSyntheseCard key={dossier.updated_at} dossier={dossier} />

      <DemarchesSyntheseCard dossier={dossier} />

      <DossierAgendaCard dossier={dossier} />

      <DossierDocumentsCard dossier={dossier} />

      <ResteAVivreCard dossierId={dossier.id} reloadKey={`${(dossier.attachments || []).length}-${dossier.co_emprunteur?.actif ? 1 : 0}`} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch" data-testid="synthese-row-2">
        <DossierRingover dossierId={dossier.id} phones={phones} />
        <DossierEmails dossierId={dossier.id} />
      </div>

      <DossierTicketsCard dossier={dossier} />

      <div data-testid="synthese-row-3">
        <DossierDiscussion dossierId={dossier.id} />
      </div>
    </div>
  );
}
